import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import {
  BillableElement,
  Board,
  BoardContext,
  BoardImages,
  ElementPlacement,
  MountType,
  PhysicalDetailsState,
} from "../../../../reducers/uploadedBoard";
import {
  ButtonContainer,
  FieldsContainer,
  UpdatePlacementFormContainer,
} from "./SelectPartStyles";
import Button from "../../../common/Button";
import { ButtonTypes, ButtonVariants } from "../../../common/Button/constants";
import FormikNumberInput from "../../../formik-form-components/FormikNumberInput";
import { Formik, FormikHelpers } from "formik";
import FormikSelect from "../../../formik-form-components/FormikSelect";
import { useUpdateBoardElementSelection } from "../../../../utils/hooks/commonly-stored/uploaded-board/updates/billOfMaterials";
import SVGViewer, { DEFAULT_FOOTPRINT_CURSOR } from "../../../common/SVGViewer";
import useFootprintForPart from "../../../../utils/hooks/footprints/forPart";
import { UpdatePlacementProperties } from "./ElementProperties";
import { setFocus } from "../../../../reducers/svgViewer";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks/store";
import { useBoardFromContext } from "../../../../utils/hooks/commonly-stored/uploaded-board/board";
import LoadingIcon from "../../../common/LoadingIcon";
import { AddFootprintAssociationModal } from "../../../internal/Footprints/AddFootprintAssociation";
import { Footprint } from "../../../../utils/hooks/footprints/models";

const PlacementFormContents = ({
  values,
  setValues,
  handleSubmit,
  isValid,
  dirty,
  board,
  images,
  footprint,
  selectedElement,
  onCancel,
  setAddingFootprint,
  addingFootprint,
}: {
  values: ElementPlacement;
  setValues: (values: ElementPlacement) => void;
  handleSubmit: () => void;
  isValid: boolean;
  dirty: boolean;
  board: Board;
  images: BoardImages | null;
  footprint: Footprint | null;
  selectedElement: BillableElement;
  onCancel?: () => void;
  setAddingFootprint: (addingFootprint: boolean) => void;
  addingFootprint: boolean;
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (addingFootprint) {
        return;
      }
      const target = event.target as HTMLElement;
      const elementIsOnTopOfBoard = values.mount_type === MountType.TOP;
      const isTyping =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (isTyping && event.key !== "Enter") {
        return;
      }

      const MOVE_STEP = 0.05;
      const ROTATION_STEP = 45;

      switch (event.key) {
        case "q":
        case "Q":
          event.preventDefault();

          setValues({
            ...values,
            rotation_degrees:
              (elementIsOnTopOfBoard
                ? values.rotation_degrees + ROTATION_STEP
                : values.rotation_degrees - ROTATION_STEP) % 360,
          });

          break;

        case "e":
        case "E":
          event.preventDefault();

          setValues({
            ...values,
            rotation_degrees:
              (elementIsOnTopOfBoard
                ? values.rotation_degrees - ROTATION_STEP
                : values.rotation_degrees + ROTATION_STEP) % 360,
          });

          break;

        case "w":
        case "W":
          event.preventDefault();

          setValues({
            ...values,
            y: values.y + MOVE_STEP,
          });

          break;

        case "a":
        case "A":
          event.preventDefault();

          setValues({
            ...values,
            x: elementIsOnTopOfBoard
              ? values.x - MOVE_STEP
              : values.x + MOVE_STEP,
          });

          break;

        case "s":
        case "S":
          event.preventDefault();

          setValues({
            ...values,
            y: values.y - MOVE_STEP,
          });

          break;

        case "d":
        case "D":
          event.preventDefault();

          setValues({
            ...values,
            x: elementIsOnTopOfBoard
              ? values.x + MOVE_STEP
              : values.x - MOVE_STEP,
          });

          break;

        case "Enter":
          event.preventDefault();
          handleSubmit();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [values, setValues, handleSubmit, addingFootprint]);

  return (
    <UpdatePlacementFormContainer onSubmit={handleSubmit}>
      {board.physical_details_state === PhysicalDetailsState.PROCESSED && (
        <div style={{ width: "100%", height: "100%" }}>
          <SVGViewer
            svgInput={{
              svgUrl:
                (values.mount_type == MountType.TOP
                  ? images?.top?.svg
                  : images?.bottom?.svg) || undefined,
            }}
            overlay={{
              input: {
                svg: footprint?.physical_info?.svg || undefined,
                svgUrl: footprint?.physical_info?.svg
                  ? undefined
                  : DEFAULT_FOOTPRINT_CURSOR,
              },
              placement: values,
              setPlacement: (placement: ElementPlacement) =>
                void setValues(placement),
            }}
            mountType={values.mount_type}
          />
        </div>
      )}

      <FieldsContainer>
        <UpdatePlacementProperties
          element={selectedElement}
          setAddingFootprint={setAddingFootprint}
        />

        <FormikNumberInput
          name={"x"}
          label={`X-position (${EXPECTED_PLACEMENT_UNIT})`}
        />

        <FormikNumberInput
          name={"y"}
          label={`Y-position (${EXPECTED_PLACEMENT_UNIT})`}
        />

        <FormikNumberInput
          name={"rotation_degrees"}
          label={"Rotation degrees"}
          step={45}
        />

        <FormikSelect
          name={"mount_type"}
          label={"Mount side"}
          options={[
            { value: MountType.TOP, label: MountType.TOP },
            { value: MountType.BOTTOM, label: MountType.BOTTOM },
          ]}
        />

        <ButtonContainer>
          <Button
            label={"Submit"}
            type={ButtonTypes.SUBMIT}
            disabled={!isValid && !dirty}
          />

          {onCancel !== undefined && (
            <Button
              label={"Cancel"}
              variant={ButtonVariants.SECONDARY}
              onClick={onCancel}
            />
          )}
        </ButtonContainer>
      </FieldsContainer>
    </UpdatePlacementFormContainer>
  );
};

const placementSchema = Yup.object().shape({
  x: Yup.number().required(
    "You must enter an X position to update an element's placement info",
  ),
  y: Yup.number().required(
    "You must enter an Y position to update an element's placement info",
  ),
  rotation_degrees: Yup.number().required(
    "You must enter a rotation to update an element's placement info",
  ),
  mount_type: Yup.string().required(
    "You must select a mount type to update an element's placement info",
  ),
});

export const EXPECTED_PLACEMENT_UNIT = "mm";

const UpdateElementPlacement = ({
  selectedElement,
  onSubmit,
  onCancel,
}: {
  selectedElement: BillableElement;
  onSubmit?: () => void;
  onCancel?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { footprint } = useFootprintForPart(selectedElement.part_id);
  const board = useBoardFromContext().board as Board;
  const [addingFootprint, setAddingFootprint] = useState(false);
  const images = useAppSelector(
    (state) => (state.uploadedBoard.boards[board.id] as BoardContext).images,
  );

  const updateElement = useUpdateBoardElementSelection();

  const handleSubmit = (
    values: ElementPlacement,
    { resetForm }: FormikHelpers<ElementPlacement>,
  ) => {
    updateElement(
      [
        {
          elementId: selectedElement.id,
          elementName: selectedElement.name,
          doNotPopulate: selectedElement.do_not_populate,
          part_id: selectedElement.part_id,
          isConsignment: selectedElement.is_consignment,
          lockAllocation: selectedElement.lock_allocation,
          elementPlacement: values,
        },
      ],
      () => {
        onSubmit?.();
        resetForm();
      },
    );
  };

  if (
    selectedElement.placement !== null &&
    selectedElement.placement.unit !== EXPECTED_PLACEMENT_UNIT
  ) {
    throw Error(
      `Selected element ${selectedElement.name} with unit ${selectedElement.placement.unit} does not meet expected placement unit ${EXPECTED_PLACEMENT_UNIT}`,
    );
  }

  const getDefaultForMissingXPlacement = () => {
    if (board.physical_details?.dimensions) {
      return (
        (board.physical_details.dimensions.x_max -
          board.physical_details.dimensions.x_min) /
          2 +
        board.physical_details.dimensions.x_min
      );
    }
    return 0;
  };
  const getDefaultForMissingYPlacement = () => {
    if (board.physical_details?.dimensions) {
      return (
        (board.physical_details.dimensions.y_max -
          board.physical_details.dimensions.y_min) /
          2 +
        board.physical_details.dimensions.y_min
      );
    }
    return 0;
  };

  const defaultValues = {
    x: selectedElement.placement?.x || getDefaultForMissingXPlacement(),
    y: selectedElement.placement?.y || getDefaultForMissingYPlacement(),
    unit: selectedElement.placement?.unit || EXPECTED_PLACEMENT_UNIT,
    rotation_degrees: selectedElement.placement?.rotation_degrees || 0,
    mount_type: selectedElement.placement?.mount_type || MountType.TOP,
  };

  useEffect(() => {
    const focusWidth = (footprint?.physical_info?.width || 2) * 5;
    const focusHeight = (footprint?.physical_info?.height || 2) * 5;
    dispatch(
      setFocus({
        focus: {
          dimensions: {
            minX: defaultValues.x - focusWidth / 2,
            minY: defaultValues.y - focusHeight / 2,
            width: focusWidth,
            height: focusHeight,
          },
          mountType: defaultValues.mount_type,
        },
      }),
    );
  }, [
    selectedElement.id,
    defaultValues.mount_type,
    defaultValues.x,
    defaultValues.y,
    dispatch,
    footprint?.physical_info?.height,
    footprint?.physical_info?.width,
  ]);

  if (board.physical_details_state !== PhysicalDetailsState.PROCESSED) {
    return (
      <LoadingIcon
        centerHorizontally
        centerVertically
        text={
          "The board's physical details are still processing, please wait..."
        }
      />
    );
  }

  return (
    <>
      <AddFootprintAssociationModal
        partId={selectedElement.part_id as string}
        isOpen={addingFootprint}
        closeModal={() => setAddingFootprint(false)}
      />
      <Formik
        initialValues={defaultValues}
        onSubmit={handleSubmit}
        validationSchema={placementSchema}
        enableReinitialize
      >
        {({ handleSubmit, isValid, dirty, values, setValues }) => {
          return (
            <PlacementFormContents
              values={values}
              setValues={setValues}
              handleSubmit={handleSubmit}
              isValid={isValid}
              dirty={dirty}
              board={board}
              images={images}
              footprint={footprint}
              selectedElement={selectedElement}
              onCancel={onCancel}
              setAddingFootprint={setAddingFootprint}
              addingFootprint={addingFootprint}
            />
          );
        }}
      </Formik>
    </>
  );
};

export default UpdateElementPlacement;
